/**
 * 公告管理 — GET/POST /api/admin/announcements，PATCH /api/admin/announcements/:id
 */
import { useState } from 'react';
import { Button, Form, Input, Modal, Switch, Table, Tag, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface AnnRow {
  id: number;
  title: string;
  body: string;
  is_published: boolean;
  create_time: string;
  update_time: string;
}

export function AnnouncementsPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<AnnRow | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('system.announcement.create');
  const canEdit = hasNode('system.announcement.edit');

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'announcements'],
    queryFn: () => api.get<AnnRow[]>('/api/admin/announcements'),
  });

  async function onCreate(values: { title: string; body: string; is_published: boolean }) {
    try {
      await api.post('/api/admin/announcements', values);
      message.success('公告已创建');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: { title: string; body: string; is_published: boolean }) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/announcements/${editRow.id}`, values);
      message.success('已更新');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    {
      title: '状态',
      dataIndex: 'is_published',
      width: 100,
      render: (v: boolean) =>
        v ? <Tag color="green">已发布</Tag> : <Tag>草稿</Tag>,
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'op',
      width: 100,
      render: (_: unknown, row: AnnRow) =>
        canEdit ? (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditRow(row);
              editForm.setFieldsValue({
                title: row.title,
                body: row.body,
                is_published: row.is_published,
              });
            }}
          >
            编辑
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            发布公告
          </Button>
        )}
      </div>

      <Table<AnnRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={data}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={createOpen}
        title="新建公告"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ is_published: true }}
          onFinish={onCreate}
        >
          <Form.Item name="title" label="标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label="正文" rules={[{ required: true }]}>
            <Input.TextArea rows={8} />
          </Form.Item>
          <Form.Item name="is_published" label="立即发布" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title="编辑公告"
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="title" label="标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label="正文" rules={[{ required: true }]}>
            <Input.TextArea rows={8} />
          </Form.Item>
          <Form.Item name="is_published" label="发布" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
